// Angular
import {
    Component,
    OnDestroy,
    OnInit,
    Input,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
} from '@angular/core';

// Store
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../../store/index';

// Actions
import * as cartActions from '../../../../store/cart/cart.actions';
import * as RouterActions from '../../../../store/router/router.actions';

// Reducer
import { selectCartItems } from '../../../../store/cart/cart.reducer';

// Models
import { Cart, CartAction, CartStatus, CartHelper } from '../../../models/b2b/cart';
import { CartItem } from '../../../models/b2b/cartItem';
import { User } from '../../../models/b2b/user';

// Services
import { UtilsService } from '../../../services/api/utils.service';
import { SoledadUserService } from '../../../services/b2b/user.service';
import { SoledadCartService } from '../../../services/b2b/cart.service';
import { ModalsService } from '../../../../shared/services/utils/modals.service';

// Helpers
import { Subscriber } from '../../helpers/subscriber';
import { SearchType } from '../../../models/b2b/searchType';
import { EventsService } from '../../../services/utils/events.service';

import { isMobile, isUndefined } from '../../../../shared/lib/helpers/utils';
import { detectChangesOnFinalize } from '../../custom-rxjs-operators/detect-changes-on-finalize.operator';

import * as moment from 'moment';
import { combineLatest } from 'rxjs';
import { ModalOptionsHelper } from '../../../../shared/models/b2b/modalSubjectData';
import { takeWhile } from 'rxjs/operators';
import * as _ from 'lodash';

export const MAX_TIMEOUT_TIME = 30; // Minutes

@Component({
    selector: 'app-cart',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './cart.component.html',
})
export class CartComponent extends Subscriber implements OnInit, OnDestroy, OnChanges {
    @Input() user: User;

    animateAdd: boolean = false;
    cartOpen: boolean = false;
    selectOpen: boolean = false;
    initCartToAnimated: boolean = false;
    numItemsPrev: number = 0;

    // Menu Item
    searchType: SearchType;

    // Cart
    shopCart: Cart;
    items: CartItem[];
    hasItemsWithoutConfirmedStock: boolean = false;

    // Link
    presupuestosUrl: string;

    isSearchedParts: boolean = false;

    interval: any;
    intervalAge: any;

    cartStatus: CartStatus;
    CartStatus = CartStatus;

    loadingAlternativeProducts: boolean;

    constructor(
        private store: Store<AppState>,
        private utilsService: UtilsService,
        public userService: SoledadUserService,
        private eventsService: EventsService,
        private cartService: SoledadCartService,
        private modalsService: ModalsService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        if (!isUndefined(this.interval)) {
            this.localClearInterval(this.interval);
        }
    }

    ngOnInit(): void {
        this.subscribeCart();
        this.subscribeCartItems();
        this.subscribeIsSearchedParts();
        this.subscribeCartStatus();
        this.subscribeLoadingAlternativeProducts();

        this.initCartToAnimated = false;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.initCartToAnimated = true;
            this.cdr.detectChanges();
        }, 1500);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.user && changes.user.currentValue) {
            this.user = _.cloneDeep(changes.user.currentValue);
        }
        if (this.user && this.shopCart) {
            this.getPresupuestoUrl();

            if (this.user && this.user.addresses && this.user.addresses.length > 0) {
                if (this.isSearchedParts === false) {
                    this.localClearInterval(this.interval);

                    const foundParts = this.items.filter((value) => {
                        return value.parts;
                    });
                    if (foundParts.length) {
                        this.validateCartParts();
                    }
                } else {
                    this.startInterval();
                }
            }
        }
    }

    subscribeIsSearchedParts(): void {
        this.addSubscription(
            this.store
                .select((state) => state.search.isSearchedParts)
                .subscribe((isSearchedParts) => {
                    this.localClearInterval(this.interval);

                    this.isSearchedParts = isSearchedParts;

                    if (this.user && this.shopCart) {
                        if (this.user && this.user.addresses && this.user.addresses.length > 0) {
                            if (isSearchedParts) {
                                this.startInterval();
                            } else {
                                const foundParts = this.items.filter((value) => {
                                    return value.parts;
                                });
                                if (foundParts.length && !this.isSearchedParts) {
                                    this.validateCartParts();
                                }
                            }
                        }
                    }
                    this.cdr.detectChanges();
                }),
        );
    }

    startInterval(): void {
        if (isUndefined(this.interval) && this.user.checkPartsCart) {
            this.intervalAge = moment();
            this.interval = setInterval(() => {
                this.executeInterval();
            }, 5000);
        }
    }

    executeInterval(): void {
        const diff = moment().diff(this.intervalAge, 'minutes');
        // console.log({diff}, (diff > MAX_TIMEOUT_TIME));

        if (diff > MAX_TIMEOUT_TIME) {
            this.localClearInterval(this.interval);

            this.modalsService.open('alert-inactivity').subscribe(
                (data) => {
                    if (data === undefined) {
                        this.utilsService.goToMainProducts();
                    }
                },
                () => {
                    this.utilsService.goToMainProducts();
                },
            );
        } else {
            // console.log('[Cart] validateCartParts');
            this.validateCartParts();
        }
    }

    subscribeCartStatus(): void {
        this.addSubscription(
            this.store
                .select((state) => state.cart.status)
                .subscribe((status) => {
                    this.cartStatus = status;
                    if (status == CartStatus.Error) {
                        this.modalsService.open('alert-cart-error');
                    }
                    this.cdr.detectChanges();
                }),
        );
    }

    subscribeLoadingAlternativeProducts(): void {
        this.addSubscription(
            this.store
                .select((state) => state.alternativeProducts.loading)
                .subscribe((loading) => {
                    setTimeout(() => {
                        this.loadingAlternativeProducts = !!loading;
                        this.cdr.detectChanges();
                    }, 10);
                }),
        );
    }

    subscribeCart(): void {
        this.addSubscription(
            combineLatest(
                this.store.select((state) => state.cart.cart),
                this.store.select((state) => state.cart.status),
            ).subscribe(([cart, status]) => {
                if (cart && status) {
                    if (
                        status === CartStatus.Validated &&
                        cart.numItems > 0 &&
                        cart.lastAction === CartAction.ItemAdded
                    ) {
                        this.doAnimateAdd();
                    }

                    this.numItemsPrev = cart.numItems;
                    const promos = cart.promos.filter((p) => p.tipoRegalo !== 'SALDO');

                    this.shopCart = { ...cart, promos };
                    this.shopCart.promos = this.shopCart.promos.filter((p) => p.tipoRegalo !== 'SALDO');
                    if (this.user) {
                        this.getPresupuestoUrl();
                    }
                    this.cdr.detectChanges();
                }
            }),
        );
    }

    subscribeCartItems(): void {
        this.addSubscription(
            this.store.pipe(select(selectCartItems)).subscribe((items) => {
                // console.log('[Cart] ITEMS', { items });
                this.items = _.cloneDeep(items);
                this.hasItemsWithoutConfirmedStock = CartHelper.hasItemsWithoutConfirmedStock(this.items);

                this.intervalAge = moment();

                this.setObsoleteItemOptions();
                this.cdr.detectChanges();
            }),
        );
    }

    validateCartParts(): void {
        this.store.dispatch(
            new cartActions.DoValidateCart({
                user: this.user,
                cart: this.shopCart,
                parts: true,
            }),
        );
    }

    /**
     * Get all oportunities items (obsoletos) and build quantity options based on stockFull available
     */
    private setObsoleteItemOptions(): void {
        this.items.map((item) => {
            if (item.product.info.obsoleto === true) {
                item.options = this.buildQuantityOptions(1, item.product.stockFull, 1);
            }
        });
    }

    private localClearInterval(interval) {
        clearInterval(interval);
        this.interval = undefined;
    }

    doAnimateAdd() {
        if (this.initCartToAnimated) {
            this.animateAdd = true;
            this.cdr.detectChanges();
            setTimeout(() => {
                this.animateAdd = false;
                this.cdr.detectChanges();
            }, 2000);
        }
    }

    clickOpenCart() {
        if (isMobile()) {
            this.cartOpen = !this.cartOpen;
            this.cdr.detectChanges();
        }
    }

    cartEnter() {
        if (!isMobile()) {
            this.cartOpen = true;
            this.isSearchedParts ? this.validateCartParts() : '';
            this.cdr.detectChanges();
        }
    }

    cartLeave() {
        if (!isMobile()) {
            if (!this.selectOpen) {
                this.cartOpen = false;
                this.cdr.detectChanges();
            }
        }
    }

    close() {
        this.cartOpen = false;
        this.cdr.detectChanges();
    }

    clickSelect() {
        this.selectOpen = true;
        this.cdr.detectChanges();
    }

    clickItemCart(item: CartItem) {
        if (!item.parts) {
            this.eventsService.broadcast('goDetailProduct', { id: item.product.info.codigo, item: item.product });
        }
    }

    clickDelete(event: Event, item: CartItem): void {
        event.preventDefault();
        // Open Modal to confirm action
        const options = ModalOptionsHelper.create({
            title: 'modal_checkout_confirm_remove_title',
            message: 'modal_checkout_confirm_remove_text',
            showAcceptBtn: true,
            showCancelBtn: true,
        });
        this.addSubscription(
            this.modalsService
                .open('checkout-confirm-remove', options, item)
                .pipe(detectChangesOnFinalize(this.cdr))
                .pipe(
                    takeWhile((data) => data !== undefined), // continúa la suscripción hasta que se cierre el modal
                )
                .subscribe((data) => {
                    if (data && data.accepted) {
                        this.removeCartItem(<CartItem>data.data);
                    }
                }),
        );
    }

    removeCartItem(item: CartItem): void {
        this.store.dispatch(new cartActions.DoPull({ ...item }));

        setTimeout(() => {
            this.store.dispatch(
                new cartActions.DoValidateCart({
                    user: _.cloneDeep(this.user),
                    cart: _.cloneDeep(this.shopCart),
                    parts: item.parts,
                    modParts: item.parts,
                }),
            );
        }, 50);
    }

    private emptyCart(): void {
        // Clear Cart
        this.store.dispatch(new cartActions.DoClear());
        this.cartService.clear(this.user.clienteCodigo);
    }

    clickBudget(): void {
        window.open(this.presupuestosUrl, '_blank');
        setTimeout(() => {
            this.emptyCart();
        }, 500);
    }

    clickBuy(): void {
        this.store.dispatch(
            new RouterActions.Go({
                path: ['dashboard/checkout'],
            }),
        );
    }

    updateQuantity(item: CartItem, quantity: number): void {
        this.selectOpen = false;
        this.cdr.detectChanges();

        this.store.dispatch(
            new cartActions.DoUpdate({
                item: _.cloneDeep(item),
                quantity: quantity,
                type: 'quantity',
            }),
        );

        setTimeout(() => {
            this.store.dispatch(
                new cartActions.DoValidateCart({
                    user: _.cloneDeep(this.user),
                    cart: _.cloneDeep(this.shopCart),
                    parts: item.parts,
                    modParts: item.parts,
                }),
            );
        }, 50);
    }

    isToday(date: string): boolean {
        return date === 'Hoy';
    }

    isOportunities(item: CartItem): boolean {
        if (item) {
            return item.product.info.obsoleto === true;
        }
    }

    buildQuantityOptions(min: any, max: any, increment: any): any[] {
        // console.log('buildQuantityOptions', {min}, {max}, {increment});

        const quantityOptions: any[] = [];

        // Fill the array
        let pos = min;

        while (pos < max + 1) {
            quantityOptions.push({ value: pos, label: pos });
            pos += pos === min && increment > min ? increment - min : increment;
        }

        return quantityOptions;
    }

    getPresupuestoUrl(): void {
        const guid = Math.floor((1 + Math.random()) * 0x1000000000).toString(16);

        const encodedParams = this.userService.getAuthTokenExtrasFromStorage();

        let urlBase;

        // Check if there are base link in enviroment.linkedApps
        if (this.user.urlExtras) {
            urlBase = this.user.urlExtras['presupuestos'].replace('%PARAMS%', encodedParams);
        }

        // Check if there are specific link
        if (this.user.urlPresupuestos) {
            urlBase = this.user.urlPresupuestos;
        }

        if (this.user.urlPresupuestos) {
            this.user.urlPresupuestos = this.user.urlPresupuestos.replace('%PARAMS%', encodedParams);
        }

        const params = [];
        const ids = this.shopCart.items.ids;

        for (let index in ids) {
            const entity = this.shopCart.items.entities[ids[index]];
            if (entity && entity.quantity !== undefined) {
                params.push(ids[index] + ':' + entity.quantity);
            }
        }

        // Not undefined
        if (urlBase) {
            urlBase = urlBase.replace('%ARTICLES%', params.join(';')).replace('%GUID%', guid);
        }

        this.presupuestosUrl = urlBase;
        this.cdr.detectChanges();
    }
}
